import type {
    CreatorGrantablePlanClass,
    CreatorGrantPaymentSource,
    ICreatorGrantablePlan,
    IGrantCreatorSubscriptionPayload,
} from '../types/global/creatorSubscription.types';
import {
    ENTERPRISE_DURATION_PRESETS,
    resolveEnterprisePeriodEnd,
    type EnterpriseDurationPresetId,
} from './adminCampaignHostEnterprise';

export { ENTERPRISE_DURATION_PRESETS, resolveEnterprisePeriodEnd };
export type { EnterpriseDurationPresetId };

export const CREATOR_GRANTABLE_PLAN_CLASSES: CreatorGrantablePlanClass[] = ['starter', 'pro', 'pro plus'];
export const CREATOR_GRANT_PAYMENT_SOURCES: CreatorGrantPaymentSource[] = ['stripe', 'paystack'];

export function normalizeCreatorPlanClass(value: unknown): CreatorGrantablePlanClass | null {
    const v = String(value || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');
    if (v === 'basic' || v === 'starter') return 'starter';
    if (v === 'pro') return 'pro';
    if (v === 'pro+' || v === 'proplus' || v === 'pro plus' || v === 'advanced') return 'pro plus';
    return null;
}

export function normalizeCreatorGrantPaymentSource(value: unknown): CreatorGrantPaymentSource | null {
    const v = String(value || '')
        .trim()
        .toLowerCase();
    if (v === 'stripe' || v === 'paystack') return v;
    return null;
}

export function normalizeCreatorGrantablePlan(raw: unknown): ICreatorGrantablePlan | null {
    if (raw == null || typeof raw !== 'object') return null;
    const r = raw as Record<string, unknown>;
    const id = String(r._id ?? r.id ?? '');
    const name = String(r.name ?? '');
    const planClass = normalizeCreatorPlanClass(r.plan_class) || normalizeCreatorPlanClass(name);
    if (!id || !planClass) return null;
    return {
        _id: id,
        name: name || planClass,
        plan_class: planClass,
        plan_code: (r.plan_code as string) || undefined,
        amount: r.amount != null ? Number(r.amount) : undefined,
        currency: (r.currency as string) || undefined,
        interval: (r.interval as string) || undefined,
        region: (r.region as string) || undefined,
        source: normalizeCreatorGrantPaymentSource(r.source) || (r.source as string) || undefined,
        description: (r.description as string) || undefined,
        features: Array.isArray(r.features) ? (r.features as string[]) : undefined,
        active: r.active !== false,
    };
}

export function normalizeCreatorGrantablePlans(raw: unknown): ICreatorGrantablePlan[] {
    const list = Array.isArray(raw)
        ? raw
        : raw && typeof raw === 'object' && Array.isArray((raw as { data?: unknown }).data)
        ? (raw as { data: unknown[] }).data
        : [];
    return list.map(normalizeCreatorGrantablePlan).filter(Boolean) as ICreatorGrantablePlan[];
}

function isMonthlyInterval(interval?: string): boolean {
    const v = String(interval || '')
        .trim()
        .toLowerCase();
    return v === 'month' || v === 'monthly';
}

/** Prefer monthly Global (settings catalog), then non-EU, when multiple rows per class. */
function scoreGrantablePlanForPicker(plan: ICreatorGrantablePlan): number {
    let score = 0;
    if (isMonthlyInterval(plan.interval)) score += 4;
    if (String(plan.region || '').toLowerCase() === 'global') score += 2;
    if (!String(plan.plan_code || '').includes('_eu')) score += 1;
    return score;
}

/** One row per plan class for the picker (already filtered by source on the API). */
export function preferGrantablePlansForPicker(
    plans: ICreatorGrantablePlan[] | null | undefined,
): ICreatorGrantablePlan[] {
    const list = Array.isArray(plans) ? plans : [];
    const byClass = new Map<CreatorGrantablePlanClass, ICreatorGrantablePlan>();
    for (const plan of list) {
        if (!plan?.plan_class) continue;
        const existing = byClass.get(plan.plan_class);
        if (!existing || scoreGrantablePlanForPicker(plan) > scoreGrantablePlanForPicker(existing)) {
            byClass.set(plan.plan_class, plan);
        }
    }
    return CREATOR_GRANTABLE_PLAN_CLASSES.map(c => byClass.get(c)).filter(Boolean) as ICreatorGrantablePlan[];
}

export function formatCreatorGrantablePlanLabel(plan: ICreatorGrantablePlan): string {
    const currency = (plan.currency || 'USD').toUpperCase();
    const price =
        typeof plan.amount === 'number'
            ? new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency,
                  maximumFractionDigits: 2,
              }).format(Number(plan.amount) / 100)
            : null;
    const interval = plan.interval ? ` / ${plan.interval}` : '';
    return price ? `${plan.name} · ${price}${interval}` : plan.name;
}

export function buildCreatorGrantPayload(input: {
    userId: string;
    planId?: string;
    planClass?: string;
    source?: CreatorGrantPaymentSource;
    presetId: EnterpriseDurationPresetId;
    customEndIso?: string;
    startsAt?: string;
}): IGrantCreatorSubscriptionPayload | null {
    const userId = input.userId.trim();
    if (!userId) return null;
    if (!input.planId && !input.planClass) return null;
    if (!input.planId && !input.source) return null;
    const expiresAt = resolveEnterprisePeriodEnd(input.presetId, input.customEndIso);
    if (!expiresAt) return null;
    return {
        userId,
        expiresAt,
        ...(input.planId ? { planId: input.planId } : {}),
        ...(input.planClass ? { planClass: input.planClass } : {}),
        ...(input.source ? { source: input.source } : {}),
        ...(input.startsAt ? { startsAt: input.startsAt } : {}),
    };
}
